import {
  getActivityConfig,
  getActivityTitle,
  isAnswerOperation,
  isProgressOperation,
  isReasoningOperation,
  isSystemOperation,
  isTaskOperation,
  getBlockFirstLine,
  type StreamingBlock,
  type StreamingBlockId,
  type StreamingOperation,
  type StreamingOperationId,
} from "@storytell/streaming";
import { extractAgentType, resolveAgentDisplayName } from "./agentIdentity";

type T = (key: string, params?: Record<string, string>) => string;
type BlockLookup = (blockId: StreamingBlockId) => StreamingBlock | undefined;

/**
 * A sub-agent's tool rows carry a per-call activity the registry can't know, so the tool name in
 * the operation's name is what resolves.
 */
export function resolveEffectiveActivity(operation: StreamingOperation): string {
  if (getActivityConfig(operation.activity)) return operation.activity;
  if (operation.name && getActivityConfig(operation.name)) return operation.name;
  return operation.activity ?? "unknown";
}

/** A summary document's title: its first line, without the heading marks. */
export function documentTitle(text: string): string {
  return withoutHeadingMarks(firstLine(text));
}

const withoutHeadingMarks = (line: string) => line.replace(/^#+\s*/, "");

// Scans to the first non-blank line rather than splitting a whole streamed document into lines.
function firstLine(text: string): string {
  let start = 0;
  while (start < text.length) {
    const end = text.indexOf("\n", start);
    const line = text.slice(start, end === -1 ? text.length : end);
    if (line.trim()) return line;
    if (end === -1) break;
    start = end + 1;
  }
  return "";
}

/**
 * The first line of an operation's text: from its finished block, or read off the chunks of one
 * still streaming without joining them. `ended` is false while more of that line may arrive.
 */
function blockFirstLine(op: StreamingOperation, block: BlockLookup): { text: string; ended: boolean } {
  for (const blockId of op.blocks ?? []) {
    const found = block(blockId);
    if (found?.finalContent?.type === "text" && found.finalContent.text) {
      return { text: firstLine(found.finalContent.text), ended: true };
    }
    const streamed = getBlockFirstLine(blockId);
    if (streamed.text) return streamed;
  }
  return { text: "", ended: false };
}

/** A URL as host and path, without the scheme or "www.", cut in the middle past 48 characters. */
function shortUrl(url: string): string {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return url;
  }
  const short = (parsed.host.replace(/^www\./, "") + parsed.pathname).replace(/\/$/, "");
  return short.length > 48 ? `${short.slice(0, 34)}…${short.slice(-13)}` : short;
}

// A sub-agent's search rows hold only the bare query or URL as their progress text, once the call
// has one; until then the text is the tool's name in words, which the plain phrase stands for.
const QUERY_STEP_KEYS: Record<string, { query: string; plain: string }> = {
  knowledge_base_search: { query: "heartbeat.searching_files", plain: "heartbeat.searching_files_plain" },
  web_search: { query: "heartbeat.searching_web", plain: "heartbeat.searching_web_plain" },
  web_page: { query: "heartbeat.reading_page", plain: "heartbeat.reading_page_plain" },
};

/**
 * The query or URL a search step's progress text carries, or undefined while it carries only the
 * tool's name in words ("Knowledge base search") or its activity title.
 */
function stepQuery(op: StreamingOperation, activity: string, t: T): string | undefined {
  const text = op.progressMessage?.trim();
  if (!text) return undefined;
  const titleKey = getActivityTitle(activity, false);
  const names = [activity.replace(/_/g, " "), op.name.replace(/_/g, " "), titleKey ? t(titleKey) : ""];
  return names.some((name) => name && name.toLowerCase() === text.toLowerCase()) ? undefined : text;
}

// Streamed tools (summary, progress, reasoning) open their row under a capitalized title, not the
// tool name.
const stepActivity = (op: StreamingOperation): string =>
  getActivityConfig(op.name.toLowerCase()) ? op.name.toLowerCase() : resolveEffectiveActivity(op);

/** A reasoning note the main agent wrote. A sub-agent's notes belong to its card. */
export const isMainReasoningNote = (op: StreamingOperation): boolean =>
  !op.parentOperationId && isReasoningOperation(stepActivity(op));

function stepLabel(op: StreamingOperation, t: T, block: BlockLookup): string {
  const activity = stepActivity(op);
  if (activity === "progress") {
    // The note's short title lands in the progress text once the call ends; until then the
    // streaming body is all there is.
    return op.progressMessage || blockFirstLine(op, block).text || op.name;
  }
  if (activity === "summary") {
    const line = blockFirstLine(op, block);
    // A first line still streaming is only part of the title.
    const title = line.ended || op.isComplete ? withoutHeadingMarks(line.text) : "";
    return title ? t("heartbeat.writing_document", { title }) : t("heartbeat.writing_findings");
  }
  if (activity === "web_page") {
    const pageTitle = op.blocks.map((id) => block(id)?.webSearchResults?.results[0]?.title).find((title) => title);
    if (pageTitle) return t("heartbeat.reading_titled_page", { title: pageTitle });
    const url = stepQuery(op, activity, t);
    return url ? t("heartbeat.reading_page", { query: shortUrl(url) }) : t("heartbeat.reading_page_plain");
  }
  if (activity === "collaborate") return t("heartbeat.coordinating");
  if (activity === "task_board") return t("heartbeat.updating_task_board");
  const keys = QUERY_STEP_KEYS[activity];
  if (keys) {
    const query = stepQuery(op, activity, t);
    return query ? t(keys.query, { query }) : t(keys.plain);
  }
  const titleKey = getActivityTitle(activity, false);
  return op.progressMessage || (titleKey ? t(titleKey) : "") || op.name;
}

/**
 * What a sub-agent is doing, given its operations: its newest step, kept after it finishes until a
 * newer one starts, since an agent spends most of its run waiting on the model between steps.
 * Undefined before its first step.
 */
export function agentLiveStep(
  operations: readonly StreamingOperation[],
  t: T,
  block: BlockLookup
): { operationId: StreamingOperationId; label: string; done: boolean } | undefined {
  const newest = operations
    .filter((op) => !isTaskOperation(op))
    .toSorted((a, b) => (a.startedAt?.getTime() ?? a.index) - (b.startedAt?.getTime() ?? b.index))
    .at(-1);
  if (!newest) return undefined;
  return { operationId: newest.operationId, label: stepLabel(newest, t, block), done: newest.isComplete };
}

/** The key of the agent group holding `operationId`, the agent's own operation or one of its steps. */
export function groupContaining(
  groups: ReadonlyMap<string, { operations: ReadonlyArray<{ operation: StreamingOperation }> }>,
  operationId: string
): string | undefined {
  for (const [key, group] of groups) {
    if (group.operations.some((e) => e.operation.operationId === operationId)) return key;
  }
  return undefined;
}

// The cards above carry every agent's steps; the footer only previews the first few.
const MAX_AGENT_LINES = 4;

export interface HeartbeatAgentLine {
  id: StreamingOperationId;
  /** The agent card's title. */
  name: string;
  step: string;
  /** What opening the line focuses in the activity: the step shown, else the agent. */
  focusId: StreamingOperationId;
  /** The agent hasn't started a step yet. */
  idle: boolean;
  /** The step shown has finished; the agent is between steps. */
  done: boolean;
}

export interface HeartbeatStatus {
  main: string;
  /** Only while waiting on sub-agents, in start order. */
  agents: HeartbeatAgentLine[];
}

/**
 * The turn's status, or undefined while one of the main agent's steps is running: that step is
 * already a row in the timeline, so a line under it would repeat it. With only sub-agents running
 * it reads as a wait, previewing each agent's live step. The answer and reasoning streams aren't
 * steps, so a stalled one still reads as thinking.
 */
export function heartbeatStatus(
  operations: readonly StreamingOperation[],
  t: T,
  block: BlockLookup
): HeartbeatStatus | undefined {
  const running = operations.filter((op) => !op.isComplete);
  const stepRunning = running.some((op) => {
    if (op.parentOperationId || isTaskOperation(op)) return false;
    if (isProgressOperation(op.activity) || isSystemOperation(op.activity)) return false;
    const activity = resolveEffectiveActivity(op);
    return !isAnswerOperation(activity) && !isReasoningOperation(activity);
  });
  if (stepRunning) return undefined;

  const agentOps = running
    .filter(isTaskOperation)
    .toSorted((a, b) => (a.startedAt?.getTime() ?? a.index) - (b.startedAt?.getTime() ?? b.index));
  if (agentOps.length === 0) return { main: t("chat_thinking"), agents: [] };

  const stepsByAgent = new Map<string, StreamingOperation[]>();
  for (const op of operations) {
    if (op.parentOperationId) {
      const steps = stepsByAgent.get(op.parentOperationId);
      if (steps) steps.push(op);
      else stepsByAgent.set(op.parentOperationId, [op]);
    }
  }

  const agents = agentOps.slice(0, MAX_AGENT_LINES).map((agent) => {
    const steps = stepsByAgent.get(agent.operationId) ?? [];
    const step = agentLiveStep(steps, t, block);
    return {
      id: agent.operationId,
      name: resolveAgentDisplayName(agent, extractAgentType(agent), t),
      step: step?.label ?? t("heartbeat.starting"),
      focusId: step?.operationId ?? agent.operationId,
      idle: step === undefined,
      done: step?.done ?? false,
    };
  });
  const main =
    agentOps.length === 1
      ? t("heartbeat.waiting_for_agent")
      : t("heartbeat.waiting_for_agents", { count: String(agentOps.length) });
  return { main, agents };
}

export const sameHeartbeatStatus = (a: HeartbeatStatus | undefined, b: HeartbeatStatus | undefined): boolean =>
  a === b ||
  (a !== undefined &&
    b !== undefined &&
    a.main === b.main &&
    a.agents.length === b.agents.length &&
    a.agents.every((line, i) => {
      const other = b.agents[i];
      return (
        other?.id === line.id &&
        other.name === line.name &&
        other.step === line.step &&
        other.focusId === line.focusId &&
        other.idle === line.idle &&
        other.done === line.done
      );
    }));
